import { useEffect, useRef, useState } from "react";
import { NAVY } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { loadGoogleMaps } from "../lib/maps";
import { useIsMobile } from "../lib/useIsMobile";

export function TripRouteMap({ trip, route, relevantClientIds = [] }) {
  const isMobile = useIsMobile();
  const mapRef  = useRef(null);
  const mapObj  = useRef(null);
  const [clients, setClients] = useState([]);

  // Load clients with coords
  useEffect(()=>{
    const ids = [...new Set([...(relevantClientIds||[]), trip.matched_client_id].filter(Boolean))];
    if (!ids.length) { setClients([]); return; }
    fetch(`${SUPABASE_URL}/rest/v1/clients?id=in.(${ids.join(",")})&lat=not.is.null&lng=not.is.null&select=id,name,lat,lng`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json()).then(rows=>{ if(Array.isArray(rows)) setClients(rows); });
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if (!trip || !mapRef.current) return;

    loadGoogleMaps().then(()=>{
      if (!mapRef.current) return;

      // Center on trip start or midpoint
      let center = { lat: Number(trip.start_lat)||39.95, lng: Number(trip.start_lon)||-75.16 };

      if (!mapObj.current) {
        mapObj.current = new window.google.maps.Map(mapRef.current, {
          center, zoom:13, mapTypeId:"roadmap",
          gestureHandling: isMobile ? "greedy" : "auto",   // one-finger pan on phones; desktop unchanged
          styles:[{featureType:"poi",elementType:"labels",stylers:[{visibility:"off"}]}],
        });
      } else {
        mapObj.current.setCenter(center);
      }

      // Clear existing overlays
      if (mapObj.current._overlays) {
        mapObj.current._overlays.forEach((o:any)=>o.setMap(null));
      }
      mapObj.current._overlays = [];

      const bounds = new window.google.maps.LatLngBounds();

      // Draw route polyline
      if (route && route.length > 1) {
        const path = route.map((c)=>({ lat: Number(c[1]), lng: Number(c[0]) }));
        const line = new window.google.maps.Polyline({
          path,
          geodesic:     true,
          strokeColor:  trip.trip_type === "business" ? "#22C55E" : "#3B82F6",
          strokeOpacity: 0.9,
          strokeWeight:  4,
          map: mapObj.current,
        });
        mapObj.current._overlays.push(line);
        path.forEach((p:any)=>bounds.extend(p));

        // Start marker (green)
        const startM = new window.google.maps.Marker({
          position: path[0], map: mapObj.current,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale:8, fillColor:"#22C55E", fillOpacity:1, strokeColor:"white", strokeWeight:2 },
          title: "Trip Start",
        });
        mapObj.current._overlays.push(startM);

        // End marker (red)
        const endM = new window.google.maps.Marker({
          position: path[path.length-1], map: mapObj.current,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale:8, fillColor:"#EF4444", fillOpacity:1, strokeColor:"white", strokeWeight:2 },
          title: "Trip End",
        });
        mapObj.current._overlays.push(endM);
      } else if (trip.start_lat && trip.end_lat) {
        // No route data — just show start/end markers
        const sPos = { lat: Number(trip.start_lat), lng: Number(trip.start_lon) };
        const ePos = { lat: Number(trip.end_lat),   lng: Number(trip.end_lon) };
        bounds.extend(sPos); bounds.extend(ePos);
        [[sPos,"#22C55E","Trip Start"],[ePos,"#EF4444","Trip End"]].forEach(([position,fillColor,title])=>{
          mapObj.current._overlays.push(new window.google.maps.Marker({
            position, map: mapObj.current, title,
            icon: { path: window.google.maps.SymbolPath.CIRCLE, scale:8, fillColor, fillOpacity:1, strokeColor:"white", strokeWeight:2 },
          }));
        });
      }

      // Client markers
      clients.forEach((c)=>{
        const pos = { lat: Number(c.lat), lng: Number(c.lng) };
        const isMatched = c.id === trip.matched_client_id;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
          <circle cx="14" cy="14" r="13" fill="${isMatched?"#F59E0B":"#6366F1"}" stroke="white" stroke-width="2"/>
          <text x="14" y="19" text-anchor="middle" font-size="12">🐕</text>
        </svg>`;
        const marker = new window.google.maps.Marker({
          position: pos, map: mapObj.current, title: c.name,
          icon: { url:`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize:new window.google.maps.Size(28,28), anchor:new window.google.maps.Point(14,14) },
          zIndex: isMatched ? 10 : 1,
        });
        const iw = new window.google.maps.InfoWindow({
          content:`<div style="font-size:12px;padding:2px"><strong>${c.name}</strong>${isMatched?"<br><span style='color:#B45309'>✓ Matched this trip</span>":""}</div>`
        });
        marker.addListener("click",()=>iw.open(mapObj.current,marker));
        mapObj.current._overlays.push(marker);
      });

      if (!bounds.isEmpty()) mapObj.current.fitBounds(bounds, 40);
    }).catch(e=>console.error("Map error:",e));

  },[trip, route, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  const st = new Date(trip.start_time);
  const en = new Date(trip.end_time);
  const dur = Math.round((en.getTime()-st.getTime())/60000);
  // A trip with no stored GPS polyline still has start/end coords — show those instead of
  // a "Loading route..." line that never resolves (route is null, not pending).
  const hasMap = !!(route || (trip.start_lat && trip.end_lat));

  return (
    <div style={{border:"1px solid #e3e8ef",borderRadius:8,overflow:"hidden"}}>
      {/* Both rows wrap: on a 375px phone the title used to be squeezed to ~120px (4 lines)
          beside a no-wrap legend. The legend sits under the title on phones. */}
      <div style={{padding:"10px 14px",background:"#f8f9fb",borderBottom:"1px solid #e3e8ef",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:"6px 12px"}}>
        <div style={{fontSize:13,fontWeight:600,color:NAVY,minWidth:0,flex:isMobile?"1 1 100%":"0 1 auto"}}>
          🗺 Route — {st.toLocaleDateString()} {st.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})} · {Number(trip.distance_miles).toFixed(1)} mi · {dur}m
        </div>
        <div style={{display:"flex",gap:"4px 12px",flexWrap:"wrap",fontSize:11,color:"#8993a4"}}>
          <span style={{whiteSpace:"nowrap"}}><span style={{color:"#22C55E"}}>●</span> Start</span>
          <span style={{whiteSpace:"nowrap"}}><span style={{color:"#EF4444"}}>●</span> End</span>
          <span style={{whiteSpace:"nowrap"}}><span style={{color:"#F59E0B"}}>🐕</span> Matched client</span>
          <span style={{whiteSpace:"nowrap"}}><span style={{color:"#6366F1"}}>🐕</span> Other clients</span>
        </div>
      </div>
      {!route&&<div style={{padding:"8px 12px",fontSize:12,color:"#8993a4",textAlign:"center"}}>{hasMap?"No GPS polyline for this trip — showing start and end points":"No GPS route recorded for this trip"}</div>}
      <div ref={mapRef} style={{width:"100%",height:isMobile?"min(350px, 50vh)":350,display:hasMap?"block":"none"}}/>
    </div>
  );
}
